/**
 * Pure per-question analytics for the Survey Results screens (VER-109, mock-only).
 *
 * Turns a set of raw respondent answer-maps into display-ready aggregates per
 * question — option distributions, averages, NPS split, VAS spread, free-text lists —
 * plus an overall score summary (via the builder's own scoreSurvey, so the number
 * matches what the runner would compute). Framework-free so it unit-tests without React.
 *
 * There is no backend here: the input is the mock CompletedSurvey data. Keeping the
 * shape pure means the same function will work unchanged if a real endpoint feeds it later.
 */
import type { SurveyDefinition, SurveyQuestion, SurveyResponseValues } from "../builder/survey.types";
import { allQuestions, effectiveOptions } from "../builder/survey.types";
import { isAnswered, scoreSurvey } from "../builder/scoring";

/** One row in a distribution bar chart: an option and how many respondents picked it. */
export type DistributionRow = {
    label: string;
    value: number;
    count: number;
    /** Share of respondents who answered the question (0–1). */
    pct: number;
};

export type NpsSplit = {
    detractors: number;
    passives: number;
    promoters: number;
    /** Net Promoter Score: (%promoters − %detractors), rounded, range −100..100. */
    score: number;
};

export type QuestionAnalytics = {
    question: SurveyQuestion;
    /** How the result is rendered. */
    kind: "choice" | "multi" | "nps" | "vas" | "freeText";
    /** Number of respondents who answered this question. */
    answered: number;
    /** Option distribution for choice / multi / NPS-bucket displays. */
    distribution?: DistributionRow[];
    /** Mean of numeric answers (single-answer choice, VAS, NPS). */
    average?: number | null;
    /** VAS spread. */
    range?: { min: number; max: number };
    /** NPS breakdown (type === "nps"). */
    nps?: NpsSplit;
    /** Non-empty free-text answers (type === "freeText"). */
    textAnswers?: string[];
};

export type OverallScore = {
    /** Mean FINAL score across respondents who completed all required questions. */
    meanFinal: number | null;
    /** Respondents whose answer set was complete enough to score. */
    completeCount: number;
    rawRange: [number, number];
    multiplier?: number;
};

export type SurveyAnalytics = {
    respondentCount: number;
    /** True when the survey has at least one scored numeric question. */
    scored: boolean;
    overall?: OverallScore;
    questions: QuestionAnalytics[];
};

const mean = (nums: number[]): number | null =>
    nums.length === 0 ? null : nums.reduce((a, b) => a + b, 0) / nums.length;

/** Numeric answers to a single-value question, across all responses that answered it. */
const numericAnswers = (values: (SurveyResponseValues[string] | undefined)[]): number[] =>
    values.filter((v): v is number => typeof v === "number");

const npsBucket = (score: number): "detractors" | "passives" | "promoters" =>
    score <= 6 ? "detractors" : score <= 8 ? "passives" : "promoters";

const analyseQuestion = (
    survey: SurveyDefinition,
    question: SurveyQuestion,
    values: (SurveyResponseValues[string] | undefined)[],
): QuestionAnalytics => {
    const answeredValues = values.filter((v) => isAnswered(v));
    const answered = answeredValues.length;

    if (question.type === "freeText") {
        const textAnswers = answeredValues.filter((v): v is string => typeof v === "string").map((s) => s.trim());
        return { question, kind: "freeText", answered, textAnswers };
    }

    if (question.type === "nps") {
        const nums = numericAnswers(values);
        const split: NpsSplit = { detractors: 0, passives: 0, promoters: 0, score: 0 };
        for (const n of nums) split[npsBucket(n)]++;
        const total = nums.length;
        split.score = total === 0 ? 0 : Math.round(((split.promoters - split.detractors) / total) * 100);
        const distribution: DistributionRow[] = [
            { label: "Promoters (9–10)", value: 9, count: split.promoters, pct: total ? split.promoters / total : 0 },
            { label: "Passives (7–8)", value: 7, count: split.passives, pct: total ? split.passives / total : 0 },
            { label: "Detractors (0–6)", value: 0, count: split.detractors, pct: total ? split.detractors / total : 0 },
        ];
        return { question, kind: "nps", answered, nps: split, average: mean(nums), distribution };
    }

    if (question.type === "vas") {
        const nums = numericAnswers(values);
        return {
            question,
            kind: "vas",
            answered,
            average: mean(nums),
            range: nums.length ? { min: Math.min(...nums), max: Math.max(...nums) } : undefined,
        };
    }

    // choice types: likert / single / multi
    const options = effectiveOptions(survey, question);
    if (question.type === "multi") {
        const selections = values.filter((v): v is number[] => Array.isArray(v));
        const distribution: DistributionRow[] = options.map((o) => {
            const count = selections.filter((sel) => sel.includes(o.value)).length;
            return { label: o.label, value: o.value, count, pct: answered ? count / answered : 0 };
        });
        return { question, kind: "multi", answered, distribution };
    }

    // likert / single — single numeric answer
    const nums = numericAnswers(values);
    const distribution: DistributionRow[] = options.map((o) => {
        const count = nums.filter((n) => n === o.value).length;
        return { label: o.label, value: o.value, count, pct: answered ? count / answered : 0 };
    });
    return { question, kind: "choice", answered, average: mean(nums), distribution };
};

/** Aggregate a set of respondent answer-maps into display-ready analytics. */
export const aggregateSurvey = (survey: SurveyDefinition, responses: SurveyResponseValues[]): SurveyAnalytics => {
    const questions = allQuestions(survey).map((question) =>
        analyseQuestion(
            survey,
            question,
            responses.map((r) => r[question.id]),
        ),
    );

    const scored = allQuestions(survey).some((question) => question.scored && question.type !== "freeText");

    let overall: OverallScore | undefined;
    if (scored) {
        const finals: number[] = [];
        for (const response of responses) {
            const result = scoreSurvey(survey, response);
            if (result.complete && result.finalScore !== null) {
                finals.push(result.finalScore);
            }
        }
        overall = {
            meanFinal: mean(finals),
            completeCount: finals.length,
            rawRange: survey.scoring.rawRange,
            multiplier: survey.scoring.multiplier,
        };
    }

    return { respondentCount: responses.length, scored, overall, questions };
};
